import { Analytics } from '@analytics';
import { userHasRevolut } from '@api/payout';
import { connectApplePay } from '@api/taptotip';
import StripeLogo from '@assets/images/Stripe.jpg';
import CheckMark from '@assets/images/booking_widget/checkMarkGreen.svg';
import RevolutGradient from '@assets/images/payment/RevolutGradient.jpg';
import SafeAndSecure from '@assets/images/payment/SafeAndSecure.svg';
import WarningIcon from '@assets/images/warning.svg';
import TooltipWrapper from '@components/Common/TooltipWrapper';
import CountryDropdown from '@components/CountryDropdown';
import StateDropdown from '@components/StateDropdown';
import TopNav from '@components/Nav/TopNav';
import TipDirectLeftMenu from '@components/Nav/TopNav/TipDirectLeftMenu';
import RevolutWireDialogContent from '@components/TapToTip/AddRevolutDetails';
import AddToursModal from '@components/TapToTip/AddToursModal';
import GuideTours from '@components/TapToTip/GuideTours';
import PaymentOptionsSwitcher from '@components/TapToTip/PaymentOptionsSwitcher';
import { languages, RevolutCurrencies, TTTPaymentPlatforms } from '@constants';
import { CircularProgress, MenuItem, Snackbar, TextField } from '@material-ui/core';
import { AccountCircle as AccountIcon, ClearOutlined as CancelIcon } from '@material-ui/icons';
import { Alert as MuiAlert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/styles';
import { StyleBreakpoints } from '@utils/constants';
import {
  countries,
  isTippingSupportedCountry,
  relevantCurrencies,
  tippingSupportedCountries
} from '@utils/helpers';
import { isValidStateForCountry } from '@utils/states';
import { useInjectReducer } from '@utils/injectReducer';
import { useInjectSaga } from '@utils/injectSaga';
import { Field, Form, Formik } from 'formik';
import { DropzoneDialog } from 'material-ui-dropzone';
import * as R from 'ramda';
import { default as React, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import * as yup from 'yup';
import * as actions from './actions';
// import BusinessStatus from './businessStatus';
import messages from './messages';
import reducer from './reducer';
import saga from './saga';
import * as localeActions from '../../LanguageProvider/actions';

const useStyles = makeStyles(theme => ({
  outerContainer: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up(StyleBreakpoints.sm)]: {
      flexDirection: 'row'
    }
  },
  infoBlock: {
    marginBottom: 20,
    minWidth: 250,

    [theme.breakpoints.down(StyleBreakpoints.sm)]: {
      marginRight: 0
    },
    [theme.breakpoints.up(StyleBreakpoints.md)]: {
      display: 'grid',
      gridTemplateRows: '0fr'
    },

    '& .MuiStepper-root': {
      paddingLeft: 0
    }
  },
  textField: {
    marginBottom: '20px',
    marginTop: 8,
    width: '100%',
    minWidth: '250px',

    '& label.Mui-focused': {
      fontWeight: 600,
      color: '#4f46e5'
    },
    '& input': {
      fontWeight: 500
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff',
      borderRadius: '12px',

      '& fieldset': {
        borderColor: '#cbd5e1'
      },
      '&:hover fieldset': {
        borderColor: '#94a3b8'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4f46e5',
        borderWidth: '1px'
      }
    }
  },
  toursRow: {
    marginBottom: '32px',
    [theme.breakpoints.up(StyleBreakpoints.md)]: {
      order: '-1'
    }
  },
  tipPaymentContainer: {
    marginTop: '20px',
    [theme.breakpoints.up(StyleBreakpoints.md)]: {
      order: '-2'
    }
  },
  mobileLayout: {
    display: 'flex',
    [theme.breakpoints.up(StyleBreakpoints.md)]: {
      display: 'none'
    }
  },
  desktopLayout: {
    display: 'none',
    [theme.breakpoints.up(StyleBreakpoints.md)]: {
      display: 'flex'
    }
  }
}));

const initialProfile = {
  firstName: '',
  lastName: '',
  description: '',
  payment: '',
  defaultPaymentAmount: 10,
  paymentOptionEnabled: 'bespoke',
  country: 'ie',
  state: '',
  currency: 'eur',
  businessStatus: 'individual',
  taxNumber: '',
  paymentOptions: {
    paymentOptionEnabled: 'bespoke',
    quickPaymentAmounts: {
      good: 0,
      great: 0,
      amazing: 0
    },
    defaultPaymentAmount: 0
  }
};

const profileSchema = yup.object({
  firstName: yup.string().required(),
  lastName: yup.string().required(),
  description: yup.string(),
  currency: yup.string().required(),
  country: yup.string().required(),
  // Required only where a state exists to pick. `test` rather than `when` so the
  // one list drives both this and the dropdown's options — a code that is valid
  // for the US but not Canada fails here after a country change.
  state: yup
    .string()
    .test('state-for-country', 'Please select a state', function testState(value) {
      return isValidStateForCountry(this.parent.country, value);
    })
});

const TippingProfile = ({
  profile,
  updateProfile,
  isLoading,
  uploadUserProfileImage,
  connectStripeResponse,
  activeTours,
  addToursToProfile,
  user,
  fetchProfile,
  connectStripe,
  fetchActiveTours,
  updateLocale
}) => {
  const classes = useStyles();
  useInjectReducer({ key: 'taptotip', reducer });
  useInjectSaga({ key: 'taptotip', saga });
  const intl = useIntl();
  const [step, setStep] = useState(0);
  const [showAlert, setShouldShowCopyAlert] = useState(false);
  const [isDropzoneOpen, setIsDropzoneOpen] = useState(false);
  const [connectStripeResponseState, setConnectStripeResponse] = useState(connectStripeResponse);
  const mergedProfile = { ...initialProfile, ...profile };
  const [isOpen, setIsOpen] = useState(false);
  const [isEnablingApplePay, setIsEnablingApplePay] = useState(false);
  const [isApplePayEnabled, setIsApplePayEnabled] = useState(false);
  const [activePaymentPlatform, setActivePaymentPlatform] = useState('');

  const [revolutRemittanceModalIsOpen, setRevolutRemittanceModalIsOpen] = useState(false);
  const isRevolutCurrencyEnabled = Boolean(profile?.revolut?.currency);
  const hasStripeConnectCustom = !!user?.profile?.stripeConnectCustom?.id;

  useEffect(() => {
    if (!profile || profile.userId !== user.userId) {
      fetchProfile(user.userId);
    }

    if (!activeTours?.length) {
      fetchActiveTours();
    }

    if (user?.profile.stripeConnect?.stripeClientId || user?.profile.stripeConnectCustom?.id) {
      connectStripe();
    }

    Analytics.trackViewFeature('SaveProfile');
  }, []);

  useEffect(() => {
    const hasStripeConnect = () => {
      const stripeConnected =
        connectStripeResponse?.paymentIntegrationStatus === 'active' ||
        connectStripeResponseState?.paymentIntegrationStatus === 'active';
      stripeConnected && setActivePaymentPlatform(TTTPaymentPlatforms.stripe);
    };

    const hasRevolut = async () => {
      try {
        const revolutUser = await userHasRevolut(user.userId);
        if (revolutUser.exists) {
          setActivePaymentPlatform(TTTPaymentPlatforms.revolut);
        }
      } catch (e) {
        //noop
      }
    };
    // call the function
    hasStripeConnect();
    hasRevolut();
  }, []);

  const redirectStripe = redirectUrl => {
    window.location = redirectUrl;
  };

  const handleDrop = files => {
    if (files[0]) {
      uploadUserProfileImage(files[0]);
    }
    setIsDropzoneOpen(false);
  };

  const handleRevolutRemittanceClose = () => {
    setRevolutRemittanceModalIsOpen(false);
  };

  const handleTipValueSubmit = values => {
    Analytics.track('updated suggested tip amount options');
    Analytics.trackUseFeature('ChangeTippingTypes');
    updateProfile({ userId: user.userId, profile: values });
  };

  const steps = [
    {
      label: intl.formatMessage(messages.imageHeader),
      fieldName: 'image'
    },
    {
      label: intl.formatMessage(messages.firstNameHeader),
      fieldName: 'firstName'
    },
    {
      label: intl.formatMessage(messages.lastNameHeader),
      fieldName: 'lastName'
    },
    {
      label: intl.formatMessage(messages.nicknameHeader),
      fieldName: 'nickname'
    },
    {
      label: intl.formatMessage(messages.bioHeader),
      fieldName: 'description'
    },
    {
      label: intl.formatMessage(messages.paymentHeader),
      fieldName: 'currency'
    },
    {
      label: intl.formatMessage(messages.countryHeader),
      fieldName: 'country'
    }
  ];

  // `form` is threaded through for the country step alone, which owns a second
  // field (`state`) rather than getting a step of its own — a wizard page that
  // only exists for Americans and Canadians would be a strange thing to give
  // everybody.
  const getStepContent = (step, values, handleChangeField, form) => {
    switch (step) {
      case 0:
        return (
          <div>
            <div className="flex w-full justify-center">
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              <div
                className="relative w-1/2 max-w-[144px] my-5 cursor-pointer group"
                onClick={() => setIsDropzoneOpen(true)}>
                {profile?.avatarUrl ? (
                  <img
                    src={profile?.avatarUrl}
                    className="w-full rounded-full ring-4 ring-white shadow-md"
                    alt="Profile Avatar"
                  />
                ) : (
                  <AccountIcon className="!w-full !h-full text-[#d8d8d8]" />
                )}
                <div className="absolute inset-0 rounded-full bg-slate-900/45 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                  <span className="text-white text-5xl font-light leading-none">+</span>
                </div>
              </div>
            </div>
            <DropzoneDialog
              cancelButtonText={intl.formatMessage(messages.imageCancel)}
              dialogTitle={
                <div className="flex items-center">
                  <span className="text-xl font-bold text-slate-800">
                    {intl.formatMessage(messages.imageHeader)}
                  </span>
                  <CancelIcon
                    onClick={() => setIsDropzoneOpen(false)}
                    className="ml-auto cursor-pointer text-slate-500 hover:text-slate-700"
                  />
                </div>
              }
              submitButtonText={intl.formatMessage(messages.imageSubmit)}
              open={isDropzoneOpen}
              onSave={handleDrop}
              maxFileSize={5000000}
              filesLimit={1}
              onClose={() => setIsDropzoneOpen(false)}
            />
          </div>
        );
      case 1:
        return (
          <TextField
            className={classes.textField}
            label={intl.formatMessage(messages.firstNameLabel)}
            variant="outlined"
            value={values.firstName}
            onChange={e => handleChangeField(e.target.value)}
          />
        );
      case 2:
        return (
          <TextField
            className={classes.textField}
            label={intl.formatMessage(messages.lastNameLabel)}
            variant="outlined"
            value={values.lastName}
            onChange={e => handleChangeField(e.target.value)}
          />
        );
      case 3:
        return (
          <TooltipWrapper
            shouldShowTooltip={true}
            title={intl.formatMessage(messages.nicknameToolTip)}>
            <TextField
              className={classes.textField}
              label={intl.formatMessage(messages.nicknameLabel)}
              variant="outlined"
              value={values.nickname}
              inputProps={{
                maxLength: 20
              }}
              onChange={e => handleChangeField(e.target.value)}
            />
          </TooltipWrapper>
        );
      case 4:
        return (
          <TextField
            multiline
            minRows={4}
            className={classes.textField}
            label={intl.formatMessage(messages.bioLabel)}
            InputLabelProps={{ shrink: true }}
            placeholder={intl.formatMessage(messages.bioPlaceholder)}
            variant="outlined"
            value={values.description}
            onChange={e => {
              handleChangeField(e.target.value);
            }}
            inputProps={{ maxLength: 200 }}
          />
        );
      case 5:
        return (
          <TooltipWrapper
            shouldShowTooltip={isRevolutCurrencyEnabled}
            title={intl.formatMessage(messages.currencyTooltip)}>
            <TextField
              id="standard-select-currency"
              disabled={isRevolutCurrencyEnabled}
              select
              label={intl.formatMessage(messages.paymentLabel)}
              variant="outlined"
              className={classes.textField}
              value={profile?.revolut?.currency || values.currency}
              onChange={e => handleChangeField(e.target.value)}
              SelectProps={{
                MenuProps: {
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                  },
                  getContentAnchorEl: null
                }
              }}>
              {Object.entries({
                ...relevantCurrencies,
                ...(isRevolutCurrencyEnabled && { ...RevolutCurrencies })
              }).map(([key, symbol]) => (
                <MenuItem key={key} value={key}>
                  {symbol}
                </MenuItem>
              ))}
            </TextField>
          </TooltipWrapper>
        );
      case 6:
        return (
          <>
            <CountryDropdown
              id="standard-select-country"
              updateCountry={(_, value) => {
                handleChangeField(value);
                // Any change of country drops the state. Clearing only when the
                // new country has none isn't enough: US -> Canada keeps the box
                // on screen, relabelled "Province", still showing "Texas" — a
                // value the backend would then silently discard on save.
                if (value !== values.country) {
                  form.setFieldValue('state', '');
                }
              }}
              // A user already in a tipping-supported country may only move to
              // another supported country — relocating to an unsupported one would
              // strip their tipping and is rejected by the backend too, so we don't
              // even offer it. Users in an unsupported country still see the full
              // list so they can correct their country and enable tipping.
              availableCountries={
                isTippingSupportedCountry(values.country)
                  ? tippingSupportedCountries
                  : countries
              }
              disabled={hasStripeConnectCustom}
              value={values.country ?? ''}
              label={intl.formatMessage(messages.countryLabel)}
              className={classes.textField}
              SelectProps={{
                MenuProps: {
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                  },
                  getContentAnchorEl: null
                }
              }}
            />
            {/* Renders nothing outside the US and Canada. Locked once the guide
                has a Connect account, same as country: from that point Stripe
                is the authority and overwrites this on account.updated. */}
            <StateDropdown
              country={values.country}
              value={values.state}
              updateState={(_, value) => form.setFieldValue('state', value || '')}
              disabled={hasStripeConnectCustom}
              error={!isValidStateForCountry(values.country, values.state)}
            />
            {values.country && !isTippingSupportedCountry(values.country) && (
              <MuiAlert severity="info" className={classes.textField}>
                {intl.formatMessage(messages.tippingNotSupported)}
              </MuiAlert>
            )}
          </>
        );
      default:
        'Unknown step';
    }
  };

  const shouldDisplayPaymentPlatform = paymentPlatform => {
    return 'none';
    if (paymentPlatform === activePaymentPlatform || !activePaymentPlatform) {
      return 'block';
    }
    return 'none';
  };

  const validateSchema = schema => values =>
    schema
      .validate(values, {
        abortEarly: false,
        strict: false
      })
      .then(() => ({}))
      .catch(({ inner }) => {
        // if got errors then focus on the first step with error and return all errors
        if (inner.length) {
          // find step by fieldName from the error path
          window.scrollTo(0, 0);
          // `state` renders inside the country step rather than owning one, so
          // its error path has no step of its own. Without this remap findIndex
          // returns -1 and the form is scrolled to a step that doesn't exist.
          const errorField = inner[0].path === 'state' ? 'country' : inner[0].path;
          const errorStep = steps.findIndex(step => step.fieldName === errorField);
          if (errorStep !== -1) {
            setStep(errorStep);
          }
        }

        return inner.reduce(
          (errorInfo, { path, message }) => ({
            ...errorInfo,
            [path]: (errorInfo[path] || []).concat(message)
          }),
          {}
        );
      });

  const handleFormSubmit = values => {
    Analytics.track('save profile', {}, true);
    Analytics.trackUseFeature('SaveProfile');
    if (
      values.country &&
      values.firstName &&
      values.lastName &&
      values.description &&
      values.currency
    ) {
      Analytics.moengageAddUserAttribute({
        hasCompletedProfile: true
      });
    }

    updateProfile({ userId: user.userId, profile: values });
  };

  // const handleBusinessStatusFormSubmit = values => {
  //   Analytics.track('update business status', {
  //     businessStatus: values.businessStatus,
  //     isTaxNumberSet: !!values.taxNumber
  //   });

  //   Analytics.moengageAddUserAttribute({
  //     businessStatus: values.businessStatus
  //   });

  //   updateProfile({ userId: user.userId, profile: values });
  // };

  return (
    <>
      <TopNav />
      <div className={classes.outerContainer}>
        <TipDirectLeftMenu selectedTab={'Profile'} />
        {isLoading && (
          <div className="flex-1 flex items-center justify-center p-6 md:p-8 min-h-[50vh]">
            <CircularProgress />
          </div>
        )}
        {!isLoading && (
          <div className="flex-1 p-6 md:p-8 w-full max-w-7xl mx-auto min-w-0 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-start">
            <div className={classes.infoBlock}>
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-slate-800 tracking-tight">
                  {intl.formatMessage(messages.profile)}
                </h1>
                <p className="text-slate-500 mt-2">{intl.formatMessage(messages.details)}</p>
              </div>
              <div className="bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 p-6 mb-8">
                <Formik
                  initialValues={mergedProfile}
                  validateOnChange={false}
                  validate={validateSchema(profileSchema)}
                  validateOnBlur={false}
                  onSubmit={handleFormSubmit}>
                  {({ errors, touched, values }) => (
                    <Form>
                      {steps.map(({ label, fieldName }, index) => (
                        <div key={label}>
                          <div>
                            <Field name={fieldName}>
                              {({ form }) => {
                                return getStepContent(
                                  index,
                                  values,
                                  val => {
                                    form.setFieldValue(fieldName, val);
                                  },
                                  form
                                );
                              }}
                            </Field>
                            {errors[fieldName] && touched[fieldName] && (
                              <div className="text-red-600 text-sm">{errors[fieldName]}</div>
                            )}
                          </div>
                        </div>
                      ))}

                      <TextField
                        select
                        label={intl.formatMessage(messages.languagePicker)}
                        variant="outlined"
                        className={classes.textField}
                        value={intl.locale}
                        onChange={e => updateLocale(e.target.value)}
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left'
                            },
                            getContentAnchorEl: null
                          }
                        }}>
                        {languages.map(({ value, label }) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                      </TextField>

                      <div className="mt-2">
                        <button
                          type="submit"
                          className="w-full mb-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-5 py-2.5 rounded-xl shadow-sm transition-colors cursor-pointer border-0">
                          {intl.formatMessage(messages.saveChanges)}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
              {/* <div className={classes.desktopLayout}>
                <BusinessStatus
                  mergedProfile={mergedProfile}
                  updateProfile={profile => updateProfile({ userId: user.userId, profile })}
                  handleSubmit={handleBusinessStatusFormSubmit}
                  validateSchema={validateSchema}
                />
              </div> */}
              <div className={`${classes.mobileLayout} ${classes.tipPaymentContainer}`}>
                <PaymentOptionsSwitcher
                  mergedProfile={mergedProfile}
                  validateSchema={validateSchema}
                  handleSubmit={handleTipValueSubmit}
                  asForm={true}
                />
              </div>
            </div>
            <div className={classes.infoBlock}>
              <div className={classes.toursRow}>
                <GuideTours
                  tours={profile?.tours}
                  setIsOpen={isOpen => {
                    Analytics.track('profile select tours');
                    setIsOpen(isOpen);
                  }}
                />
              </div>
              {/* <div className={classes.mobileLayout}>
                <BusinessStatus
                  mergedProfile={mergedProfile}
                  updateProfile={profile => updateProfile({ userId: user.userId, profile })}
                  handleSubmit={handleBusinessStatusFormSubmit}
                  validateSchema={validateSchema}
                />
              </div> */}
              <div className={`${classes.desktopLayout} ${classes.tipPaymentContainer}`}>
                <PaymentOptionsSwitcher
                  mergedProfile={mergedProfile}
                  validateSchema={validateSchema}
                  handleSubmit={handleTipValueSubmit}
                  asForm={true}
                />
              </div>

              <div className="my-5 text-xs text-slate-500" style={{ display: 'none' }}>
                {intl.formatMessage(messages.paymentIntegrationStatus)}:{' '}
                {connectStripeResponseState?.paymentIntegrationStatus === 'active' ||
                connectStripeResponse?.paymentIntegrationStatus === 'active' ? (
                  <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-md text-xs font-bold border border-solid border-emerald-200/55">
                    {intl.formatMessage(messages.active)}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 px-2.5 py-1 rounded-md text-xs font-bold border border-solid border-slate-200/55">
                    {intl.formatMessage(messages.inactive)}
                  </span>
                )}
              </div>
              <div className="flex flex-col justify-around mb-5">
                {(connectStripeResponseState?.paymentIntegrationStatus === 'active_with_problems' ||
                  connectStripeResponse?.paymentIntegrationStatus === 'active_with_problems') && (
                  <MuiAlert severity="warning" style={{ marginBottom: 16, borderRadius: '16px' }}>
                    Additional Information Required. Stripe requires some additional details to verify your account and ensure your payouts aren't interrupted. Click the Stripe card below to complete setup.
                  </MuiAlert>
                )}
                <div
                  className="max-w-[347px] cursor-pointer relative overflow-hidden bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 hover:shadow-md transition-shadow"
                  role="button"
                  tabIndex="0"
                  style={{
                    display: shouldDisplayPaymentPlatform(TTTPaymentPlatforms.stripe)
                  }}
                  onKeyDown={redirectStripe}
                  onClick={async () => {
                    setShouldShowCopyAlert(true);
                    const response = await connectStripe();
                    setConnectStripeResponse(response);
                    setShouldShowCopyAlert(false);

                    redirectStripe(response.redirectUrl);
                  }}>
                  <img
                    src={StripeLogo}
                    alt="stripe connect"
                    className="mb-5 max-w-full rounded-t-3xl"
                  />
                  <div className="mx-7 mb-7 text-xs text-slate-400">
                    <div className="text-sm font-semibold text-slate-800 mb-1">
                      {intl.formatMessage(messages.stripeConnect)}
                    </div>
                    {intl.formatMessage(messages.stripeConnectDescription)}
                  </div>
                  <div className="flex flex-row-reverse pr-5 mb-5 text-xs font-semibold text-slate-500">
                    {(connectStripeResponseState?.paymentIntegrationStatus === 'active_with_problems' ||
                      connectStripeResponse?.paymentIntegrationStatus === 'active_with_problems')
                      ? <span className="text-amber-500 font-bold">Action Required</span>
                      : intl.formatMessage(messages.getStarted)}
                  </div>
                </div>
                <div
                  style={{
                    display: shouldDisplayPaymentPlatform(TTTPaymentPlatforms.stripe)
                  }}
                  className="max-w-[347px] relative bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 mt-4 px-4 py-5">
                  <div className="flex flex-row">
                    <img className="w-[22px] h-[19px] my-auto" alt="" src={WarningIcon} />
                    <div className="text-[11px] ml-3 text-slate-800 tracking-tight">
                      {intl.formatMessage(messages.stripeConnectMessaging)}
                    </div>
                  </div>
                </div>
                {(connectStripeResponseState?.paymentIntegrationStatus === 'active' ||
                  connectStripeResponse?.paymentIntegrationStatus === 'active') && (
                  <div className="max-w-[347px] relative bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 mt-4 px-4 py-5">
                    <div className="text-sm font-semibold text-slate-800 mb-1">Payment types</div>
                    <div className="flex items-center h-8 text-xs font-bold text-slate-600">
                      <div className="inline-block mr-2 w-3.5 h-3.5 rounded-full bg-emerald-500" />
                      <div className="flex-1">Card</div>
                    </div>
                    <div className="flex items-center h-8 text-xs font-bold text-slate-600">
                      <div className="inline-block mr-2 w-3.5 h-3.5 rounded-full bg-emerald-500" />
                      <div className="flex-1">Google Pay</div>
                    </div>
                    <div className="flex items-center h-8 text-xs font-bold text-slate-600">
                      <div
                        className={`inline-block mr-2 w-3.5 h-3.5 rounded-full ${
                          isApplePayEnabled || profile?.stripeConnect?.isApplePayEnabled
                            ? 'bg-emerald-500'
                            : 'bg-amber-500'
                        }`}
                      />
                      <div className="flex-1">Apple Pay</div>
                      {!isApplePayEnabled && !profile?.stripeConnect?.isApplePayEnabled && (
                        <button
                          onClick={async () => {
                            setIsEnablingApplePay(true);
                            await connectApplePay();
                            setIsApplePayEnabled(true);
                            setIsEnablingApplePay(false);
                          }}
                          className="ml-auto bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-3 py-1.5 rounded-xl text-xs shadow-sm transition-colors cursor-pointer border-0">
                          {isEnablingApplePay ? 'Loading' : 'Enable'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div
                className="text-sm font-medium text-slate-500 mt-8 mb-8"
                style={{
                  display: shouldDisplayPaymentPlatform(TTTPaymentPlatforms.revolut)
                }}>
                {intl.formatMessage(messages.getPaidWithRevolut)}
              </div>
              <div className="flex flex-col justify-around mb-5">
                <div
                  className="max-w-[347px] cursor-pointer relative overflow-hidden bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 hover:shadow-md transition-shadow"
                  role="button"
                  tabIndex="0"
                  onClick={() => {
                    setRevolutRemittanceModalIsOpen(true);
                  }}
                  style={{
                    display: shouldDisplayPaymentPlatform(TTTPaymentPlatforms.revolut)
                  }}>
                  <div className="absolute right-0 z-10 mt-3.5 flex w-fit flex-row justify-end pr-3 h-[22px]">
                    <img
                      className="w-auto h-[26px] -mt-px mr-2"
                      src={SafeAndSecure}
                      alt="Cvc icon"
                    />
                    {activePaymentPlatform ? (
                      <div className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-md text-xs font-bold border border-solid border-emerald-200/55">
                        {intl.formatMessage(messages.active)}
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-1 bg-slate-100 text-slate-500 px-2.5 py-1 rounded-md text-xs font-bold border border-solid border-slate-200/55">
                        {intl.formatMessage(messages.notActive)}
                      </div>
                    )}
                  </div>
                  <img
                    src={RevolutGradient}
                    alt="stripe connect"
                    className="mb-5 max-w-full rounded-t-3xl"
                  />
                  <div className="mx-7 mb-7 text-xs text-slate-400">
                    <div className="text-sm font-semibold text-slate-800 mb-1">
                      {intl.formatMessage(messages.revolut)}
                    </div>
                    {intl.formatMessage(messages.addRevolutDetails)}
                  </div>
                  <div className="flex flex-row-reverse pr-5 mb-5 text-xs font-semibold text-slate-500">
                    {intl.formatMessage(messages.getStarted)}
                  </div>
                </div>
                <div className="mb-8">
                  <RevolutWireDialogContent
                    userId={user.userId}
                    isOpen={revolutRemittanceModalIsOpen}
                    onClose={handleRevolutRemittanceClose}
                  />
                </div>
                {activePaymentPlatform === TTTPaymentPlatforms.revolut && (
                  <div className="mb-8">
                    <div className="max-w-[347px] bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 p-5">
                      <div className="w-full bg-white flex flex-col justify-center text-center px-7 pt-3.5 pb-8 rounded-xl shadow-sm border border-solid border-slate-200">
                        <img className="w-[23px] h-auto mx-auto mb-2" src={CheckMark} />
                        <div className="text-sm font-semibold text-emerald-800 mb-2">
                          {intl.formatMessage(messages.revolutSynced)}
                        </div>
                        <div className="text-xs font-medium text-emerald-700">
                          {intl.formatMessage(messages.selectRevolut)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <Snackbar
        open={showAlert}
        onClose={() => {
          setShouldShowCopyAlert(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <MuiAlert
          variant="filled"
          elevation={6}
          severity="info"
          onClose={() => {
            setShouldShowCopyAlert(false);
          }}>
          {intl.formatMessage(messages.connecting)}
        </MuiAlert>
      </Snackbar>

      <AddToursModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        activeTours={activeTours}
        profile={profile}
        addToursToProfile={tourIds => addToursToProfile(user.userId, tourIds)}
        userId={user.userId}
      />
    </>
  );
};

const mapStateToProps = state => ({
  profile: state.taptotip?.profile,
  isLoading: state.taptotip?.isLoading,
  connectStripeResponse: state.taptotip?.connectStripeResponse,
  activeTours: state.taptotip?.activeTours,
  user: state.user?.user,
  tours: state.taptotip?.tours
});

const mapDispatchToProps = dispatch => ({
  fetchProfile: userId => dispatch(actions.fetchProfile(userId)),
  updateProfile: R.compose(
    dispatch,
    actions.updateProfile
  ),
  uploadUserProfileImage: image => dispatch(actions.uploadUserProfileImage(image)),
  connectStripe: () => dispatch(actions.connectStripe()),
  fetchActiveTours: () => dispatch(actions.fetchActiveTours()),
  addToursToProfile: (userId, tourIds) => dispatch(actions.addToursToProfile({ userId, tourIds })),
  updateLocale: locale => dispatch(localeActions.changeLocale(locale))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TippingProfile);
